/**
 * Phase 2 WU-2.1 — error-display contract.
 *
 * Three layers:
 *   1. `showApiError(err, opts)` — pop a toast. Default for failed
 *      admin-API mutators where the error is transient and the user
 *      can retry.
 *   2. `<FormErrorInline error={err} />` — render the same message
 *      inline above a form section. Default for blocking validation
 *      errors that the operator needs to read while editing.
 *   3. `getApiErrorMessage(err, opts)` — pure helper that returns the
 *      string the other two would show. Use it when you want to compose
 *      your own UI (e.g., a custom modal) or log the message somewhere.
 *
 * Status-code policy:
 *   - 401 → returns "Redirecting to sign in…". Callers should NOT toast
 *     401s — api-client.ts handles the redirect via reload().
 *   - 4xx (other) → use the response body's `message` first, then `error`,
 *     then the fallback. Custom `formatBody` lets callers handle
 *     structured payloads (e.g. R-65 `invalid_paths` with per-path
 *     reasons, R-62 `rasterizer_unavailable` with missingModule).
 *   - 5xx → "Server error — code XYZ. Try again or contact support."
 *   - Network failure (TypeError with "fetch" in message) →
 *     "Connection lost — check your network and try again."
 *   - Anything else → fallback (default "Something went wrong.").
 */
import { ApiError } from "./api-client";
import { toast } from "@/components/Toaster";

export interface ApiErrorOptions {
  fallback?: string;
  /**
   * Custom body formatter for structured 4xx payloads. Return a string
   * to override the default extraction; return `undefined` to fall back
   * to the standard `message` / `error` lookup.
   */
  formatBody?: (body: unknown) => string | undefined;
}

const DEFAULT_FALLBACK = "Something went wrong.";

export function getApiErrorMessage(err: unknown, opts: ApiErrorOptions = {}): string {
  const fallback = opts.fallback ?? DEFAULT_FALLBACK;

  if (err instanceof ApiError) {
    if (err.status === 401) return "Redirecting to sign in…";

    if (err.status >= 400 && err.status < 500) {
      const custom = opts.formatBody?.(err.body);
      if (typeof custom === "string" && custom.length > 0) return custom;
      const msg = extractBodyMessage(err.body);
      if (msg) return msg;
      return fallback;
    }

    if (err.status >= 500) {
      return `Server error — code ${err.status}. Try again or contact support.`;
    }
  }

  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    return "Connection lost — check your network and try again.";
  }

  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function showApiError(err: unknown, opts: ApiErrorOptions = {}): void {
  // 401 is handled by api-client redirect; suppress the toast to avoid
  // a confusing "Redirecting…" flash on a page that's about to unload.
  if (err instanceof ApiError && err.status === 401) return;
  toast.error(getApiErrorMessage(err, opts));
}

/**
 * Structured error-code reader for ApiError bodies. API routes signal
 * machine-readable failures as `{ code: "team_name_conflict" }`-style
 * bodies; call sites that branch on a specific code should use this
 * instead of hand-casting `err.body` at every catch block. Returns
 * undefined for non-ApiError values and bodies without a string `code`.
 */
export function getApiErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const body = err.body;
  if (!body || typeof body !== "object") return undefined;
  const code = (body as Record<string, unknown>)["code"];
  return typeof code === "string" ? code : undefined;
}

function extractBodyMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const rec = body as Record<string, unknown>;
  const message = rec["message"];
  if (typeof message === "string" && message.length > 0) return message;
  const error = rec["error"];
  if (typeof error === "string" && error.length > 0) return error;
  return undefined;
}
